import {
  Button,
  ConfirmModal,
  EditPencil,
  TablePaginationContext,
  UnLink,
} from '@/components';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { RowDef } from '@/components/TableV2/TableV2';
import { RoutePath } from '@/core/router';
import { AutomationCost } from '@/modules/Infrastructure/domain';
import { useGetInfrastructureCostsByAutomation } from '@/modules/Infrastructure/queries';
import { useDisassociateAutomationInfrastructureCost } from '@/modules/Infrastructure/queries/use-disassociate-automation-infrastructure-cost';
import { DataWithPagination } from '@/shared/types';
import { trunc } from '@/utils';
import { format } from 'date-fns';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AssociateInfrastructureCostModalButton } from './components/AssociateAutomationInfrastructureCostModalButton';
import { EditAutomationCostModal } from './components/EditAutomationInfrastructureCostModal';

type AutomationInfrastructureCostTabProps = {
  automationId: number;
};

export const AutomationInfrastructureCostTab = ({
  automationId,
}: AutomationInfrastructureCostTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCostId, setSelectedCostId] = useState<number>();
  const [selectedEditCost, setSelectedEditCost] = useState<AutomationCost>();

  const { mutate: disassociateAutomationInfrastructureCost } =
    useDisassociateAutomationInfrastructureCost();

  const {
    page: pageNumber,
    tableSort,
    searchTerm,
  } = useContext(TablePaginationContext);

  const sort = tableSort[0];

  const { data: costs, isLoading } = useGetInfrastructureCostsByAutomation({
    automationId,
    pageNumber: pageNumber,
    filter: searchTerm,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const columns = useMemo<SortableColumnDef<AutomationCost>[]>(
    () => [
      {
        id: 'name',
        name: t('cost_name'),
        accessor: 'name',
        alignment: 'left',
      },
      {
        id: 'startDate',
        name: t('start_date'),
        accessor: 'startDate',
        renderRow: (row) =>
          row.startDate ? format(row.startDate, 'MMMM d, yyyy') : 'N/A',
      },
      {
        id: 'endDate',
        name: t('end_date'),
        accessor: 'endDate',
        renderRow: (row) =>
          row.endDate ? format(row.endDate, 'MMMM d, yyyy') : t('on_going'),
      },
      {
        id: 'costType',
        name: t('type'),
        accessor: 'costType',
        alignment: 'left',
      },
      {
        id: 'amount',
        name: t('amount'),
        accessor: 'amount',
        renderRow: (row) => `${trunc(row.amount)}€`,
        alignment: 'left',
      },
      {
        id: 'recurrence',
        name: t('recurrence'),
        accessor: 'amountRecurrence',
        renderRow: (row) => !!row.amountRecurrence && t(row.amountRecurrence),
        alignment: 'left',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => t(row.status ? 'active' : 'inactive'),
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <div className="flex items-center gap-2">
            <Button
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedEditCost(row);
              }}
            >
              <EditPencil />
            </Button>
            <Button
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedCostId(row.id);
              }}
            >
              <UnLink />
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  const page = useMemo<
    DataWithPagination<RowDef<AutomationCost>[]> | undefined
  >(
    () =>
      costs && {
        ...costs,
        items: costs.items.map((item) => ({
          ...item,
          onClick: () =>
            navigate(RoutePath.infrastructureCostDetail(item.id.toString())),
        })),
      },
    [costs, navigate],
  );

  return (
    <>
      <PagedTable
        title={t('infrastructure_costs')}
        columns={columns}
        page={page}
        isLoading={isLoading}
        emptyStateMessage={t('no_costs_available')}
        actions={
          <AssociateInfrastructureCostModalButton automationId={automationId} />
        }
      />
      <ConfirmModal
        isOpen={!!selectedCostId}
        title={t('please_confirm_action')}
        message={t('confirm_automation_infrastructure_cost_disassociate')}
        handleClose={() => setSelectedCostId(undefined)}
        onConfirm={() => {
          if (selectedCostId) {
            disassociateAutomationInfrastructureCost({
              automationId,
              infrastructureCostId: selectedCostId,
            });
          }

          setSelectedCostId(undefined);
        }}
      />
      {selectedEditCost && (
        <EditAutomationCostModal
          isOpen={!!selectedEditCost}
          automationId={automationId}
          infrastructureCostId={selectedEditCost.id}
          startDate={selectedEditCost.startDate}
          endDate={selectedEditCost.endDate}
          onClose={() => setSelectedEditCost(undefined)}
        />
      )}
    </>
  );
};
